import { useEffect, useRef, useState, type RefObject } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface InfiniteScrollProps {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  className?: string
  rootMargin?: string
  label?: string
  root?: RefObject<Element | null>
  error?: boolean
}

function scrollParent(element: Element | null): Element | null {
  for (let node = element?.parentElement ?? null; node; node = node.parentElement) {
    if (node === document.body || node === document.documentElement) return null
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
  }
  return null
}

export function InfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  className,
  rootMargin = '600px',
  label,
  root,
  error = false
}: InfiniteScrollProps) {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [isNear, setIsNear] = useState(false)

  useEffect(() => {
    const element = sentinelRef.current
    if (!element || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(entries => setIsNear(entries.some(entry => entry.isIntersecting)), {
      root: root ? root.current : scrollParent(element),
      rootMargin
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [root, rootMargin, hasNextPage])

  useEffect(() => {
    if (isNear && hasNextPage && !isFetchingNextPage && !error) fetchNextPage()
  }, [isNear, hasNextPage, isFetchingNextPage, fetchNextPage, error])

  if (!hasNextPage) return null

  return (
    <div ref={sentinelRef} className={cn('flex items-center justify-center py-6', className)}>
      {isFetchingNextPage ? (
        <span className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando mais...
        </span>
      ) : error ? (
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="text-sm text-muted-foreground">Não foi possível carregar mais itens.</span>
          <Button variant="outline" size="sm" onClick={fetchNextPage}>
            Tentar novamente
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={fetchNextPage}>
          {label ?? 'Carregar mais'}
        </Button>
      )}
    </div>
  )
}
